import { useCallback, useEffect, useState } from "react";
import { Loader2, Activity, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { DripDashboard, ComebackReport } from "@/components/DripDashboard";
import { EmailBroadcast } from "@/components/EmailBroadcast";
import { EmailLog } from "@/components/EmailLog";
import { ReferralLeaderboard } from "@/components/ReferralLeaderboard";
import { StaleOffers } from "@/components/StaleOffers";

const KIND_KEYS = {
  login: "activity.kind_login",
  couple_update: "activity.kind_couple",
  bird_update: "activity.kind_bird",
};

function kindLabel(t, kind) {
  if (KIND_KEYS[kind]) return t(KIND_KEYS[kind]);
  return (kind || "").replace(/_/g, " ");
}

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

function durationLabel(min) {
  if (min === null || min === undefined) return "—";
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)}h ${min % 60}m`;
}

export default function AdminActivityPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/user-activity");
      setRows(data.users || []);
    } catch (_e) {
      void _e;
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") return <Navigate to="/couples" replace />;

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const onlineCount = (rows || []).filter((r) => r.online).length;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-activity-page">
      <DripDashboard />
      <ComebackReport />
      <ReferralLeaderboard />
      <StaleOffers />
      <EmailBroadcast />
      <EmailLog />
      <div className="border-t border-app-border" />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-espresso flex items-center gap-2">
            <Activity className="w-7 h-7 text-olive" />
            {t("activity.title")}
          </h1>
          <p className="text-sm text-espresso/70 mt-1">
            {t("activity.sub")} · <span className="text-olive font-semibold">{onlineCount} {t("activity.online")}</span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="activity-refresh-button">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1 rtl:mr-0 rtl:ml-1" />}
          {t("activity.refresh")}
        </Button>
      </div>

      {rows === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-espresso/50" />
      ) : (
        <div className="space-y-2" data-testid="activity-users-list">
          {rows.map((r, i) => (
            <div key={r.id} className="bg-white rounded-xl border border-app-border" data-testid={`activity-user-${i}`}>
              <button
                type="button"
                onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))}
                className="w-full flex items-center gap-3 p-4 text-left rtl:text-right"
                data-testid={`activity-user-toggle-${i}`}
              >
                {expanded[r.id] ? <ChevronDown className="w-4 h-4 text-espresso/40 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-espresso/40 flex-shrink-0 rtl:rotate-180" />}
                <span
                  className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${r.online ? "bg-green-500" : "bg-gray-300"}`}
                  data-testid={`status-dot-${i}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-espresso truncate">
                    {r.name}
                    <span className={`ml-2 rtl:ml-0 rtl:mr-2 text-[10px] font-bold uppercase ${r.online ? "text-green-600" : "text-espresso/40"}`}>
                      {r.online ? t("activity.online") : t("activity.offline")}
                    </span>
                  </div>
                  <div className="text-xs text-espresso/50 truncate">{r.email}</div>
                </div>
                <div className="ml-auto rtl:ml-0 rtl:mr-auto text-right rtl:text-left flex-shrink-0">
                  <div className="text-[11px] text-espresso/60">
                    {t("activity.last_login")}: <span className="font-medium text-espresso">{fmt(r.last_login)}</span>
                  </div>
                  <div className="text-[11px] text-espresso/60">
                    {t("activity.duration")}: <span className="font-medium text-espresso">{durationLabel(r.session_minutes)}</span>
                  </div>
                </div>
              </button>

              {expanded[r.id] && (
                <div className="border-t border-app-border px-4 py-3" data-testid={`activity-actions-${i}`}>
                  <div className="text-[10px] uppercase tracking-widest text-olive font-bold mb-2">
                    {t("activity.actions")}
                  </div>
                  {r.actions.length === 0 ? (
                    <div className="text-xs text-espresso/50">{t("activity.none")}</div>
                  ) : (
                    <ul className="space-y-1">
                      {r.actions.map((a, j) => (
                        <li key={j} className="flex items-center gap-2 text-xs text-espresso/80">
                          <span className="w-1.5 h-1.5 rounded-full bg-olive/50 flex-shrink-0" />
                          <span className="font-medium">{kindLabel(t, a.kind)}</span>
                          {a.target && a.kind !== "login" && <span className="text-espresso/60">— {a.target}</span>}
                          <span className="ml-auto rtl:ml-0 rtl:mr-auto text-espresso/40">{fmt(a.created_at)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
